import { useTranslation } from 'react-i18next';
import { Fingerprint } from 'lucide-react';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { useGetVoiceSamples } from '@utils/api/organizations/speakers/voice-profiles';

interface Props {
  speakerId: number;
}

/** How many voice samples one directory speaker has.
 *
 * The speaker list response carries no sample count (the backend's `Speaker`
 * model has no such column), so the count is read per row. That is one small
 * admin-only GET per visible row — acceptable because the column only renders
 * when the feature is enabled for an admin, the responses are cached under the
 * same key the edit dialog uses, and these list routes are not rate-limited.
 *
 * A failure renders an em-dash rather than an error: an unavailable count must
 * not break the directory grid. */
export const VoiceSampleCountCell = ({ speakerId }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetVoiceSamples(speakerId);

  if (isLoading)
    return (
      <Text as="span" size="sm" tone="muted">
        …
      </Text>
    );
  if (isError)
    return (
      <Text as="span" size="sm" tone="muted">
        —
      </Text>
    );

  const count = data?.count ?? 0;

  if (count === 0) {
    return (
      <Text as="span" size="sm" tone="muted">
        —
      </Text>
    );
  }

  return (
    <Tooltip label={t('voice-sample-count', { count })}>
      <span className="inline-flex items-center gap-1">
        <Fingerprint size={12} aria-hidden />
        <Text as="span" size="sm">
          {count}
        </Text>
      </span>
    </Tooltip>
  );
};
